import React, { useContext } from "react";
import { Box, Link as MuiLink, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import {
  copyrightText,
  KivuI18nContext,
  localizedPath,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import {
  CONTACT_EMAIL,
  CONTACT_PHONE,
  CONTACT_PHONE_HREF
} from "../../constants/contactInfo";
import { getKivunovaWebsiteUrl } from "../../Utils/kivunovaWebsite";
import {
  WELCOME_FOOTER_COLUMNS,
  type WelcomeFooterLink
} from "./welcomeFooterContent";
import {
  welcomeFooterBottomSx,
  welcomeFooterColumnTitleSx,
  welcomeFooterLinkSx,
  welcomeFooterMainSx,
  welcomeSectionContentSx
} from "./welcomeSectionStyles";

const WelcomeFooter: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext);

  const pathFor = (route: string) =>
    localizedPath(route, language, defaultLang);

  const shouldShowLink = (link: WelcomeFooterLink) => {
    if (link.id === "phone") {
      return Boolean(CONTACT_PHONE);
    }
    if (link.id === "email") {
      return Boolean(CONTACT_EMAIL);
    }
    return true;
  };

  const resolveLink = (link: WelcomeFooterLink) => {
    if (link.id === "phone" && CONTACT_PHONE) {
      const phoneHref = CONTACT_PHONE_HREF || CONTACT_PHONE.replace(/\s/g, "");
      return (
        <MuiLink href={`tel:${phoneHref}`} sx={welcomeFooterLinkSx}>
          {CONTACT_PHONE}
        </MuiLink>
      );
    }

    if (link.id === "email" && CONTACT_EMAIL) {
      return (
        <MuiLink href={`mailto:${CONTACT_EMAIL}`} sx={welcomeFooterLinkSx}>
          {CONTACT_EMAIL}
        </MuiLink>
      );
    }

    if (link.kivunovaPath) {
      return (
        <MuiLink
          href={getKivunovaWebsiteUrl(
            link.kivunovaPath,
            language || defaultLang || "en"
          )}
          target="_blank"
          rel="noopener noreferrer"
          sx={welcomeFooterLinkSx}
          data-testid={`welcome-footer-link-${link.id}`}
        >
          {t(link.labelKey, link.labelFallback)}
        </MuiLink>
      );
    }

    if (link.route) {
      return (
        <MuiLink
          component={Link}
          to={pathFor(link.route)}
          sx={welcomeFooterLinkSx}
          data-testid={`welcome-footer-link-${link.id}`}
        >
          {t(link.labelKey, link.labelFallback)}
        </MuiLink>
      );
    }

    if (link.href) {
      return (
        <MuiLink href={link.href} sx={welcomeFooterLinkSx}>
          {t(link.labelKey, link.labelFallback)}
        </MuiLink>
      );
    }

    return (
      <Typography component="span" sx={welcomeFooterLinkSx}>
        {t(link.labelKey, link.labelFallback)}
      </Typography>
    );
  };

  return (
    <Box
      component="footer"
      data-testid="welcome-footer"
      sx={{ width: "100%", flexShrink: 0 }}
    >
      <Box sx={welcomeFooterMainSx}>
        <Box sx={welcomeSectionContentSx}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)"
              },
              gap: { xs: 3, md: 4 }
            }}
          >
            {WELCOME_FOOTER_COLUMNS.map((column) => (
              <Box key={column.id}>
                <Typography component="h3" sx={welcomeFooterColumnTitleSx}>
                  {t(column.titleKey, column.titleFallback)}
                </Typography>
                <Box
                  component="nav"
                  aria-label={t(column.titleKey, column.titleFallback)}
                >
                  {column.links.filter(shouldShowLink).map((link) => (
                    <Box key={link.id} sx={{ mb: 0.25 }}>
                      {resolveLink(link)}
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <Box sx={welcomeFooterBottomSx}>
        <Typography
          variant="caption"
          sx={{ textAlign: "center", width: "100%" }}
          data-testid="welcome-footer-copyright"
        >
          {copyrightText("Kivunova")}
        </Typography>
      </Box>
    </Box>
  );
};

export default WelcomeFooter;
